class BoostSearchDrawer extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.drawerClass = "wt-drawer-search";
    this.drawer = document.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--active`;
    this.classParentDrawerActive = `${this.drawerClass}--parent-active`;
    this.pageOverlayClass = "search-overlay";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;

    this.triggerQuery = [
      ".wt-header__search-trigger",
      ".wt-header__search__close",
      ".search-results__terms",
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);

    // Boost Search configuration
    this.boostApiUrl = "https://services.mybcapps.com/bc-sf-filter/search/suggest";
    this.shopDomain = this.dataset.shopDomain || window.Shopify.shop;
    this.cachedResults = {};

    // Elements
    this.input = this.querySelector('input[name="q"]');
    this.clearButton = this.querySelector(".wt-header__search__clear-button");
    this.closeButton = this.querySelector(".wt-header__search__close");
    this.submitArrow = this.querySelector(".wt-header__search__submit-arrow");
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.mainTrigger = this.querySelector(".wt-header__search-trigger");
    this.searchResultTop = this.querySelector(".search-result-top");
    this.statusElement = this.querySelector(".predictive-search-status");
    // AQ-1075: the live-region text lives in its own child node so that
    // announcing "Loading..."/result counts never has to touch
    // .statusElement's other child, .search-result-top — see setLiveRegionText.
    this.statusTextElement = this.querySelector(".predictive-search-status__text");

    this.isVisibleClearButton = false;
    this.toggleTabindexElements = [this.input, this.closeButton];

    this.timeOut = null;

    this.setupEventListeners();
    this.init();
  }

  disconnectedCallback() {
    if (this.onDocumentClick) {
      document.removeEventListener("click", this.onDocumentClick);
    }
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input:not([type='hidden']), select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      setTabindex(this.toggleTabindexElements, "-1");
      setTabindex([this.mainTrigger], "0");
      this.isOpen = false;
      // AQ-1081: reset the field and results whenever the drawer closes
      // (X button, Escape, click-outside, or re-clicking the search
      // trigger all funnel through here) so the previous query never
      // reappears next time the drawer is opened.
      this.clearResults();
      setTimeout(() => {
        this.mainTrigger.focus();
      }, 0);
    } else {
      this.setAttribute("open", "");
      setTabindex(this.toggleTabindexElements, "0");
      setTabindex([this.mainTrigger], "-1");
      this.input.focus();
      this.input.setSelectionRange(
        this.input.value.length,
        this.input.value.length,
      );
      this.isOpen = true;
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
    console.log('parentElement', this.parentElement);
    if (this.parentElement) {
      this.parentElement.classList.toggle(this.classParentDrawerActive);
    }
  }

  init() {
    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.clearButton.addEventListener("click", () => {
      this.input.value = "";
      this.clearButton.style.display = "none";
      this.isVisibleClearButton = false;
      this.clearResults();
    });

    // Arrow button submits the search
    if (this.submitArrow) {
      this.submitArrow.addEventListener("click", (e) => {
        e.preventDefault();
        const query = this.getQuery();
        if (query.length) {
          window.location.href = `/search?q=${encodeURIComponent(query)}&type=product`;
        }
      });
    }

    // AQ-1060: clicking outside the overlay closes it, alongside the X and
    // Escape. The dimmed backdrop is the .search-overlay-on::before pseudo
    // element, so there is no node to bind to — listen on the document and
    // ignore anything inside the drawer or on a trigger (a trigger click
    // bubbles here after it has already opened the drawer).
    this.onDocumentClick = (e) => {
      if (!this.isOpen || !(e.target instanceof Element)) return;
      if (e.target.closest(`.${this.drawerClass}`)) return;
      if (e.target.closest(this.triggerQuery)) return;

      this.toggleDrawerClasses();
    };
    document.addEventListener("click", this.onDocumentClick);

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (this.isOpen) {
          if (e.shiftKey && document.activeElement === first) {
            last.focus();
            e.preventDefault();
          } else if (!e.shiftKey && document.activeElement === last) {
            first.focus();
            e.preventDefault();
          }
        }
      }
    });

    this.input.addEventListener("input", () => {
      if (this.input.value.length > 0 && !this.isVisibleClearButton) {
        this.clearButton.style.display = "flex";
        this.clearButton.setAttribute("tabindex", "0");
        this.isVisibleClearButton = true;
      } else if (this.input.value.length === 0 && this.isVisibleClearButton) {
        this.clearButton.style.display = "none";
        this.clearButton.setAttribute("tabindex", "-1");
        this.isVisibleClearButton = false;
      }
    });
  }

  setupEventListeners() {
    const form = this.querySelector("form.boost-search-form");
    if (form) {
      form.addEventListener("submit", this.onFormSubmit.bind(this));
    }

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (Shopify.designMode) {
      document.addEventListener(
        "shopify:section:load",
        this.saveSuggestionMenuInDesignMode.bind(this),
      );
    }
  }

  saveSuggestionMenuInDesignMode() {
    if (this.body.classList.contains(this.activeOverlayBodyClass))
      this.drawer.classList.toggle(this.classDrawerActive);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (searchTerm.length) {
      this.getBoostSearchResults(searchTerm);
      this.removeAttribute("empty");
    } else {
      this.clearResults();
    }
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
    }
  }

  async getBoostSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    try {
      const params = new URLSearchParams({
        q: searchTerm,
        shop: this.shopDomain,
        limit: 24,
        page: 1,
        event_type: 'suggest',
        suggest_types: 'products,pages,collections,suggestions,did_you_mean',
        suggest_fields: 'title,product_type,vendor,tags',
        dym_limit: 3
      });

      const response = await fetch(`${this.boostApiUrl}?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const resultsMarkup = this.buildBoostSearchHTML(data, searchTerm);

      this.cachedResults[queryKey] = resultsMarkup;
      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      console.error('Boost Search Error:', error);
      this.renderSearchResults('<div class="search-result-error">Unable to load search results. Please try again.</div>');
    }
  }

  buildBoostSearchHTML(data, searchTerm) {
    const products = data.products || [];
    const collections = data.collections || [];
    const suggestions = data.did_you_mean || [];
    const totalResults = products.length + collections.length + suggestions.length;

    if (totalResults === 0) {
      return `
        <div id="predictive-search-results" class="search-result search-result--no-results">
          <span data-predictive-search-live-region-count-value>
            No results found for "${this.escapeHtml(searchTerm)}"
          </span>
        </div>
      `;
    }

    // Shared "View all" button markup — rendered twice: once inside
    // .boost-search-suggestions for desktop, once pinned to the bottom of
    // the drawer for mobile (see .only-desktop / .only-mobile in
    // _boost-search.scss).
    const viewAllButtonHTML = `
      <button aria-label="View all for '${this.escapeHtml(searchTerm)}'" class="boost-search-view-all-link btn--arrow" tabindex="0">
        <span class="boost-view-all-text">VIEW ALL FOR &ldquo;${this.escapeHtml(searchTerm).toUpperCase()}&rdquo;</span>
      </button>
    `;

    let html = '<div id="predictive-search-results" class="search-result boost-search-result">';

    // Create two-column layout
    html += '<div class="boost-search-grid">';

    // Left column: Products section
    if (products.length > 0) {
      html += `
        <div class="boost-search-products">
          <h2 class="search-result__title">Products</h2>
          <div class="boost-search-products-grid" role="listbox">
      `;

      products.slice(0, 4).forEach((product, index) => {
        html += this.buildProductCard(product, index + 1, searchTerm);
      });

      html += '</div></div>';
    }

    // Right column: Suggestions section
    if (collections.length > 0 || suggestions.length > 0) {
      html += '<div class="boost-search-suggestions">';
      html += '<h2 class="search-result__title">Suggestions</h2>';
      html += '<ul class="boost-search-suggestions-list">';

      // Add collections as suggestions
      if (collections.length > 0) {
        collections.slice(0, 4).forEach(collection => {
          html += `
            <li class="boost-search-suggestion-item">
              <a href="/collections/${collection.handle}">${this.highlightSearchTerm(collection.title, searchTerm)}</a>
            </li>
          `;
        });
      }

      // Add search suggestions
      if (suggestions.length > 0) {
        suggestions.forEach(suggestion => {
          html += `
            <li class="boost-search-suggestion-item">
              <a href="/search?q=${encodeURIComponent(suggestion)}">${this.highlightSearchTerm(suggestion, searchTerm)}</a>
            </li>
          `;
        });
      }

      html += '</ul>';

      // Desktop-only "View all" — nested inside the suggestions column.
      html += `<div class="boost-search-view-all only-desktop">${viewAllButtonHTML}</div>`;

      html += '</div>'; // Close boost-search-suggestions
    }

    html += '</div>'; // Close boost-search-grid

    // Mobile-only "View all" — pinned to the bottom of the drawer.
    html += `<div class="boost-search-view-all only-mobile">${viewAllButtonHTML}</div>`;

    html += '</div>';

    return html;
  }

  buildProductCard(product, index, searchTerm) {
    const firstImage = product.images_info && product.images_info.length > 0
      ? product.images_info[0].src
      : '';

    const secondImage = product.images_info && product.images_info.length > 1
      ? product.images_info[1].src
      : firstImage;
      
    const firstVariant = product.variants && product.variants.length > 0
      ? product.variants[0]
      : null;

    const price = firstVariant ? parseFloat(firstVariant.price) : product.price_min;
    const compareAtPrice = firstVariant?.compare_at_price
      ? parseFloat(firstVariant.compare_at_price)
      : product.compare_at_price_max;

    const isOnSale = compareAtPrice && compareAtPrice > price;
    const isBestSeller = product.tags && product.tags.some(tag =>
      tag.toLowerCase().includes('bestseller') || tag.toLowerCase().includes('best seller')
    );

    const formattedPrice = this.formatPrice(price);

    return `
      <a href="/products/${product.handle}" class="boost-product-card" role="option" aria-selected="false">
        ${isBestSeller ? '<div class="boost-product-badge">BEST SELLER</div>' : ''}
        <div class="boost-product-image">
          ${firstImage ? `<img src="${firstImage}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : ''}
          ${secondImage ? `<img src="${secondImage}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : ''}
        </div>
        <div class="boost-product-info">
          <span class="boost-product-title">${this.escapeHtml(product.title)}</span>
          <div class="boost-product-price">${formattedPrice}</div>
        </div>
      </a>
    `;
  }

  formatPrice(price) {
    if (!price) return '';
    const numPrice = parseFloat(price);
    // Show whole number if no cents, otherwise show 2 decimals
    const formatted = numPrice % 1 === 0 ? `$${numPrice}` : `$${numPrice.toFixed(2)}`;
    return formatted;
  }

  highlightSearchTerm(text, searchTerm) {
    if (!text || !searchTerm) return this.escapeHtml(text);

    const escapedText = this.escapeHtml(text);
    const regex = new RegExp(`(${this.escapeHtml(searchTerm)})`, 'gi');
    return escapedText.replace(regex, '<strong class="highlight">$1</strong>');
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  setLiveRegionLoadingState() {
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text") || "Loading...";
    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    // AQ-1075: this used to write to .statusElement (.predictive-search-status)
    // itself via textContent, which also wipes out .statusElement's other
    // child, the "Best Sellers" default suggestions (.search-result-top) —
    // on the very next getBoostSearchResults() call, e.g. when backspacing
    // back down to an empty query and clearResults() tries to restore it,
    // there was nothing left of the original markup (with its already-loaded
    // images) to restore, only a stale pre-open clone taken in the
    // constructor. Write to the dedicated text node instead so
    // .search-result-top is never touched.
    if (this.statusTextElement) {
      this.statusTextElement.setAttribute("aria-hidden", "false");
      this.statusTextElement.textContent = statusText;

      this.timeOut = setTimeout(() => {
        this.statusTextElement.setAttribute("aria-hidden", "true");
      }, 1000);
    }
  }

  renderSearchResults(resultsMarkup) {
    // AQ-1081: an in-flight Boost Search request can resolve after the
    // drawer has already been closed (and clearResults() has reset the
    // field/state) — drop the stale response instead of re-populating a
    // closed drawer with the previous query's results.
    if (!this.isOpen) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    this.setLiveRegionResults();

    // Add event listener to "View all" button(s) — there are now up to two
    // in the DOM at once (.only-desktop inside the suggestions column and
    // .only-mobile pinned to the bottom), only one of which is visible per
    // breakpoint, so wire up every match rather than just the first.
    const viewAllButtons = this.predictiveSearchResults.querySelectorAll('.boost-search-view-all-link');
    viewAllButtons.forEach((viewAllButton) => {
      viewAllButton.addEventListener('click', () => {
        const searchTerm = this.getQuery();
        window.location.href = `/search?q=${encodeURIComponent(searchTerm)}`;
      });
    });
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const countElement = this.querySelector("[data-predictive-search-live-region-count-value]");
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  clearResults() {
    this.input.value = "";
    // AQ-1075: dropping [loading]/[results] is what re-shows
    // .predictive-search-status (the "Best Sellers" defaults) — see the
    // &[loading], &[results] rule in _boost-search.scss. .search-result-top
    // inside it was never touched while hidden, so it just reappears as-is.
    this.removeAttribute("loading");
    this.removeAttribute("results");
    this.setAttribute("empty", true);

    if (this.statusTextElement) {
      this.statusTextElement.textContent = "";
    }

    clearTimeout(this.timeOut);
    setTimeout(() => {
      if (this.statusTextElement) {
        this.statusTextElement.removeAttribute("aria-hidden");
      }
    }, 300);
  }
}

customElements.define("boost-search-drawer", BoostSearchDrawer);
